import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Button } from '../../catalyst/button';
import type { UserListItem } from '@avisoma/shared';
import backend from '../../services/backend';
import { PasswordRevealDialog } from './PasswordRevealDialog';

export function ResetPasswordDialog({
  user,
  open,
  onClose,
}: {
  user: UserListItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const reset = useMutation({
    mutationFn: () => backend.resetUserPassword(user!.id).then((res) => res.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNewPassword(data.generatedPassword);
    },
  });

  const closeAll = () => {
    setNewPassword(null);
    onClose();
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open && !newPassword} onClose={onClose}>
        <DialogTitle>Reset password for {user.username}?</DialogTitle>
        <DialogBody>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            A new system-generated password will replace the existing one. The user will be unable to log in
            with their current password until you give them the new one.
          </p>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button color="blue" onClick={() => reset.mutate()} disabled={reset.isPending}>
            {reset.isPending ? 'Resetting…' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>

      {newPassword && (
        <PasswordRevealDialog
          open={true}
          username={user.username}
          password={newPassword}
          title="Password reset"
          onClose={closeAll}
        />
      )}
    </>
  );
}
